import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { t, type IntlService } from 'ember-intl';
import { Chip } from 'tio-ui/components/buttons';
import Component from '@glimmer/component';
import lastFourDigits from 'tio-common/helpers/last-four-digits';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import type TasApplicationModel from 'tio-common/models/tas-application';

export interface ProgramsApplicationRowSignature {
  Args: {
    applications: TasApplicationModel[];
    expenseType: string;
  };
}

export default class ProgramApplicationsRow extends Component<ProgramsApplicationRowSignature> {
  @service declare intl: IntlService;

  statusText = (state: string): string => {
    if (this.intl.exists(`common.status_message.to.${state}`)) {
      return this.intl.t(`common.status_message.to.${state}`);
    }

    return this.intl.t('common.status_message.to.TAS.ProgramInstanceState.UPDATED_ON');
  };

  sortTasApplications(applications: TasApplicationModel[]) {
    const apps = applications ?? [];

    const nonAbandoned = apps.filter((a) => a.state !== 'TAS.ApplicationState.ABANDONED');
    const abandoned = apps.filter((a) => a.state === 'TAS.ApplicationState.ABANDONED');

    return [...nonAbandoned, ...abandoned];
  }

  <template>
    <p class="uppercase mt-4 text-sm">
      {{@expenseType}}
    </p>
    <ul role="list" class="flex flex-col gap-y-6 py-4">
      {{#each (this.sortTasApplications @applications) as |application|}}
        <li class="group relative flex gap-x-4">
          <div class="absolute -bottom-6 left-0 top-0 flex w-6 justify-center last:bottom-0">
            <div class="w-px bg-gray-200 group-last:hidden"></div>
          </div>
          <div class="relative flex size-6 flex-none items-center justify-center bg-white">
            <div class="size-1.5 rounded-full bg-gray-100 ring-1 ring-gray-300"></div>
          </div>
          <div>
            <div class="flex items-center gap-x-2">
              <LinkTo
                @route="authenticated.tas.applications.show"
                @model={{application.id}}
                class="font-semibold text-lg text-ocean-600 hover:underline"
              >
                {{String application.displayNameOrSemesterCode}}
              </LinkTo>
              <Chip @intent={{application.badgeIntent}}>
                {{application.badgeLabel}}
              </Chip>
            </div>
            <p class="text-sm text-tio-gray-400 self-end">
              {{t "tas.application.id_no"}}{{lastFourDigits application.id}}
            </p>
            <p class="text-sm">
              <span>
                {{! TODO: this is clunky. Need to review labels}}
                {{this.statusText application.latestStateChangeLabel}}
                {{t "common.on"}}
              </span>
              {{safeFormatDate
                application.latestStateChangeDate
                month="long"
                day="2-digit"
                year="numeric"
              }}
            </p>
          </div>
        </li>
      {{/each}}
    </ul>
  </template>
}
